import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Modal,
  Box,
  Button,
  Grid,
  Typography,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import './styles.scss'
import { toast } from "react-toastify";
 
interface IPROPS {
  open: any;
  setLifitinMobileNumber: any;
  onClose: any;
  callhandler: any;
  liftingMobileNumber: any;
  displayNumber?: string; // Masked number for display (for agents)
  isReadOnly?: boolean; // Make dialpad read-only for agents
  setMaskedLiftingMobileNumber?: any; // To clear masked number when user types new number
}
 
const DialPad: React.FC<IPROPS> = ({
  open,
  setLifitinMobileNumber,
  onClose,
  callhandler,
  liftingMobileNumber,
  displayNumber,
  isReadOnly = false,
  setMaskedLiftingMobileNumber,
}) => {
  const [dialNumber, setDialNumber] = useState('');
  const [highlightKey, setHighlightKey] = useState<string | null>(null);
  // Track if user has started typing a new number (for agents)
  const [hasUserTyped, setHasUserTyped] = useState(false);
  // Track previous open state to detect when modal opens
  const prevOpenRef = useRef(false);

  useEffect(() => {
    // Only initialize when modal opens (open changes from false to true)
    // This prevents resetting the number when user is typing
    if (open && !prevOpenRef.current) {
      // Reset typing state when modal opens with new number
      setHasUserTyped(false);
      
      // For display, use displayNumber if provided (masked for agents)
      // Otherwise use the real liftingMobileNumber
      if (displayNumber) {
        setDialNumber(displayNumber);
      } else if (liftingMobileNumber) {
        setDialNumber(liftingMobileNumber);
      } else {
        setDialNumber('');
      }
    }
    prevOpenRef.current = open;
  }, [open, liftingMobileNumber, displayNumber])
 
  const dialPadData = [
    { digit: "1"},
    { digit: "2"},
    { digit: "3"},
    { digit: "4"},
    { digit: "5"},
    { digit: "6"},
    { digit: "7"},
    { digit: "8"},
    { digit: "9"},
    { digit: "*"},
    { digit: "0"},
    { digit: "#"},
  ];
 
  const dialNumberRef = useRef(dialNumber);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
 
 
  const handleDial = (value: string) => {
    if (/^[0-9*#]$/.test(value)) {
      // For agents (isReadOnly): when they start typing, clear masked number and start fresh
      if (isReadOnly && !hasUserTyped) {
        setHasUserTyped(true);
        setDialNumber(value); // Start fresh with the typed digit
        setLifitinMobileNumber(value); // Update real number for call
        localStorage.setItem("liftingMobileNumber", value);
        // Also clear the masked number so new number shows without mask
        if (setMaskedLiftingMobileNumber) {
          setMaskedLiftingMobileNumber('');
          localStorage.setItem("maskedLiftingMobileNumber", '');
        }
      } else {
        setDialNumber((prev) => {
          if (prev.length < 10) {
            return prev + value;
          }
          return prev;
        });
      }
    }
  };
  
  const deleteLastDigit = () => {
    // For agents: if they haven't typed yet, clear masked number and start fresh
    if (isReadOnly && !hasUserTyped) {
      setHasUserTyped(true);
      setDialNumber('');
      setLifitinMobileNumber('');
      localStorage.setItem("liftingMobileNumber", '');
      // Also clear the masked number
      if (setMaskedLiftingMobileNumber) {
        setMaskedLiftingMobileNumber('');
        localStorage.setItem("maskedLiftingMobileNumber", '');
      }
      return;
    }
    
    setDialNumber((prev) => prev.slice(0, -1));
  };
  
  const startCall = useCallback(() => {
    callhandler();
    
  }, [callhandler]);
 
  useEffect(() => {
    dialNumberRef.current = dialNumber;
  }, [dialNumber]);
 
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      const key = event.key;
      
      if (key === "Enter") {
        startCall();
        return;
      }
      
      if (/^[0-9*#]$/.test(key)) {
        // For agents (isReadOnly): when they start typing, clear masked number and start fresh
        if (isReadOnly && !hasUserTyped) {
          setHasUserTyped(true);
          setDialNumber(key); // Start fresh with the typed digit
          setLifitinMobileNumber(key);
          localStorage.setItem("liftingMobileNumber", key);
          // Also clear the masked number so new number shows without mask
          if (setMaskedLiftingMobileNumber) {
            setMaskedLiftingMobileNumber('');
            localStorage.setItem("maskedLiftingMobileNumber", '');
          }
        } else {
          setDialNumber((prev) => {
            if (prev.length < 10) return prev + key;
            return prev;
          });
        }
      } else if (key === "Backspace") {
        // For agents: if they haven't typed yet, clear masked number and start fresh
        if (isReadOnly && !hasUserTyped) {
          setHasUserTyped(true);
          setDialNumber('');
          setLifitinMobileNumber('');
          localStorage.setItem("liftingMobileNumber", '');
          // Also clear the masked number
          if (setMaskedLiftingMobileNumber) {
            setMaskedLiftingMobileNumber('');
            localStorage.setItem("maskedLiftingMobileNumber", '');
          }
          return;
        }
        setDialNumber((prev) => prev.slice(0, -1));
      }
    },
    [startCall, isReadOnly, hasUserTyped, setLifitinMobileNumber, setMaskedLiftingMobileNumber]
  );
  
 
 
 
  const handleKeyUp = useCallback(() => {
    setHighlightKey(null); // Remove the highlight when key is released
  }, []);
 
  const handlePaste = useCallback((event: ClipboardEvent) => {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData("text") || "";
    const numericText = pastedText.replace(/[^0-9*#]/g, "");
    
    if (!numericText) return;
  
    // For agents (isReadOnly): when they paste, clear masked number and use pasted number
    if (isReadOnly && !hasUserTyped) {
      if (numericText.length > 10) {
        toast.error("Please paste valid number");
        return;
      }
      setHasUserTyped(true);
      setDialNumber(numericText); // Replace with pasted number
      setLifitinMobileNumber(numericText);
      localStorage.setItem("liftingMobileNumber", numericText);
      // Also clear the masked number so new number shows without mask
      if (setMaskedLiftingMobileNumber) {
        setMaskedLiftingMobileNumber('');
        localStorage.setItem("maskedLiftingMobileNumber", '');
      }
    } else {
      setDialNumber((prev) => {
        const totalLength = prev.length + numericText.length;
    
        if (totalLength > 10) {
          toast.error("Please paste valid number");
          return prev;
        }
    
        return prev + numericText; // Append valid characters
      });
    }
  }, [isReadOnly, hasUserTyped, setLifitinMobileNumber, setMaskedLiftingMobileNumber]);
  
  
  
  useEffect(() => {
    if (open) {
      window.addEventListener("keydown", handleKeyDown);
      window.addEventListener("keyup", handleKeyUp);
      window.addEventListener("paste", handlePaste);
    }
  
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("paste", handlePaste);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [open, handleKeyDown, handleKeyUp, handlePaste]);
  
 
  useEffect(() => {
    // Only sync when the modal is open to prevent race conditions on mount
    if (!open) return;
    
    // For agents (isReadOnly): only sync if they have explicitly typed a new number
    // This preserves the original liftingMobileNumber when showing masked number
    if (isReadOnly && !hasUserTyped) return;
    
    // For non-agents: always sync dialNumber to liftingMobileNumber
    setLifitinMobileNumber(dialNumber);
    localStorage.setItem("liftingMobileNumber", dialNumber);
  }, [open, dialNumber, setLifitinMobileNumber, isReadOnly, hasUserTyped]);
 
  return (
    <div>
      <Modal open={open} onClose={onClose}>
        <Box
          sx={{
            position: "absolute",
            top: "40%",
            left: "20%",
            transform: "translate(-50%, -50%)",
            width: 270,
            bgcolor: "background.paper",
            borderRadius: "8px",
            boxShadow: 24,
            padding: "8px 16px",
            // p: 2,
          }}
        >
       <Typography
  variant="h5"
  textAlign="center"
  sx={{
    // mb: 2,
    borderRadius: "4px",
    padding: "8px",
    position: "relative",
    minHeight: "40px",
  }}
>
  {dialNumber || (
    <span
      style={{
        display: "inline-block",
        width: "2px", // Vertical line width
        height: "1.5rem", // Line height
        // border: "1px solid green",
        backgroundColor: "#000",
        animation: "blink 1s step-end infinite", // Blinking animation
      }}
    ></span>
  )}
</Typography>
 
          {/* Dial Pad */}
          <Grid container spacing={2}>
            {dialPadData.map(({ digit }) => (
              <Grid item xs={4} key={digit} style={{paddingTop: "0px"}}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => handleDial(digit)}
                  sx={{
                    height: 70,
                    fontSize: "1.2rem",
                    borderRadius: "50%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor:
                      highlightKey === digit ? "#cfe8fc" : "transparent",
                    transition: "background-color 0.2s ease",
                    border: "none",
                    paddingTop: "0"
                  }}
                >
                  <span>{digit}</span>
                  <Typography
                    variant="caption"
                    sx={{ fontSize: "0.75rem", color: "#666" }}
                  >
                    {/* {letters} */}
                  </Typography>
                </Button>
              </Grid>
            ))}
          </Grid>
 
          <Box
            sx={{
              display: "flex",
              width: "100%",
              // marginTop: "20px",
              alignItems: "center",
            }}
          >
            <div style={{ width: "60%" }}>
              <div style={{ display: "flex", justifyContent: "end" }}>
                <i
                  className="bi bi-telephone-fill"
                  onClick={startCall}
                  style={{
                    color: "white",
                    fontSize: "18px",
                    padding: "20px",
                    borderRadius: "50%",
                    // background: "#rgb(54 193 46)",
                    background: "#2bbf5a"
                  }}
                ></i>
              </div>
            </div>
 
            <div style={{ flex: "1", justifyContent: "center" }}>
              {dialNumber && (
                <IconButton
                  color="error"
                  onClick={deleteLastDigit}
                  sx={{
                    height: 50,
                    width: 50,
                    padding: 0,
                    borderRadius: "50%",
                    backgroundColor: "transparent",
                    marginLeft: "25px",
                  }}
                >
                  <CloseIcon />
                </IconButton>
              )}
            </div>
          </Box>
        </Box>
      </Modal>
    </div>
  );
};
 
export default DialPad;
 